import { cn } from '@lib/cn';
import { Button } from '@shared/ui';
import { useState } from 'react';

import type { MetaFact } from '../model/report-overview';

/**
 * The report's masthead: eyebrow, title, the one-sentence lede and the meta
 * strip, with the actions on the right. No card — the page itself is the
 * document; the actions are not part of what gets printed.
 */
export function ReportHeader({
    eyebrow,
    title,
    lede,
    meta,
    onPrint,
    onCsv,
    onJson,
    shareUrl,
}: {
    eyebrow: string;
    title: string;
    lede: string;
    meta: readonly MetaFact[];
    onPrint: () => void;
    onCsv: () => void;
    onJson: () => void;
    shareUrl: string;
}) {
    const [linkState, setLinkState] = useState<'idle' | 'copied' | 'failed'>('idle');
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setLinkState('copied');
        } catch {
            setLinkState('failed');
        }
    };

    return (
        <header className="grid gap-3.5">
            <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-accent-icon">
                    {eyebrow}
                </span>
                <div className="flex flex-wrap gap-2 print:hidden">
                    <Button variant="ghost" size="sm" onClick={() => void copyLink()}>
                        {linkState === 'copied'
                            ? 'Link copied'
                            : linkState === 'failed'
                              ? 'Copy failed'
                              : 'Copy link'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onJson}>
                        Download JSON
                    </Button>
                    <Button variant="ghost" size="sm" onClick={onCsv}>
                        Download CSV
                    </Button>
                    <Button size="sm" onClick={onPrint}>
                        Print
                    </Button>
                </div>
            </div>
            <h1 className="text-balance text-[clamp(26px,3.4vw,34px)] font-semibold leading-tight tracking-tight text-content">
                {title}
            </h1>
            <p className="max-w-[68ch] text-[15px] leading-relaxed text-content-muted">{lede}</p>
            <dl className="flex flex-wrap gap-x-[22px] gap-y-2 pt-1 text-[13px]">
                {meta.map((fact) => (
                    <div key={fact.label} className="flex min-w-0 gap-1.5">
                        <dt className="whitespace-nowrap text-content-muted">{fact.label}</dt>
                        <dd
                            className={cn(
                                'min-w-0 break-words font-medium text-content',
                                fact.mono && 'font-mono text-[12px]',
                            )}
                        >
                            {fact.value}
                        </dd>
                    </div>
                ))}
            </dl>
        </header>
    );
}
